import { useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { Folder, FolderPlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { useRoutes } from "@/contexts/use-routes"
import { useCreateProject } from "@/hooks/use-projects"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { getProjectBasePath } from "@/utils/route-helpers"

type ProjectsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionType: string
}

export function ProjectsDialog({ open, onOpenChange, sessionType }: ProjectsDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { routes } = useRoutes()

  const [newProjectName, setNewProjectName] = useState("")

  useEffect(() => {
    if (!open) {
      setNewProjectName("")
    }
  }, [open])

  const createProject = useCreateProject()

  const handleCreate = () => {
    createProject.mutate(
      { name: newProjectName.trim(), session_type: sessionType },
      {
        onSuccess: async (data) => {
          logger.debug("ProjectsDialog: project created", { id: data.id })
          setNewProjectName("")
          onOpenChange(false)
          // The project page reads the cached projects list; land on it only
          // after the refetch so it doesn't flash "Project Not Found".
          await queryClient.invalidateQueries({ queryKey: queryKeys.projects(sessionType) })
          const projectBasePath = getProjectBasePath(sessionType, routes)
          void navigate(`${projectBasePath}/${data.id}`)
        },
        onError: (error) => {
          toast.error(t("projects.create_error"), {
            description: error.message,
          })
        },
      }
    )
  }

  const canCreate = useMemo(() => newProjectName.trim().length > 0, [newProjectName])

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="size-4" /> {t("projects.title")}
            </DialogTitle>
            <DialogDescription>{t("projects.dialog_description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                data-testid="project-name-input"
                placeholder={t("projects.new_project_name_placeholder")}
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canCreate && !createProject.isPending) {
                    logger.debug("ProjectsDialog: Enter pressed - create project")
                    handleCreate()
                  }
                }}
              />
              <Button
                data-testid="create-project-button"
                disabled={!canCreate || createProject.isPending}
                onClick={() => {
                  logger.debug("ProjectsDialog: Create button clicked")
                  handleCreate()
                }}
              >
                <FolderPlus className="mr-2 size-4" /> {t("projects.create")}
                {createProject.isPending && <Spinner className="ml-2" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
